// The public landing page at the apex `/`.
//
// Everything decorative here is progressive: the page reads fine with this
// bundle missing — the reveal classes fall back to visible content when the
// observer never runs, and the form is a plain form that this file only
// intercepts. What the bundle adds is motion (the shoal, the bubbles, a little
// parallax) and the contact form's inline reply.
import { byId, input, maybe } from '../lib/dom.ts';

const isStill = matchMedia('(prefers-reduced-motion: reduce)').matches;
// Both, not either: iPads with a trackpad have a fine pointer and hover.
const canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;

function random(low: number, high: number): number {
  return low + Math.random() * (high - low);
}

// ── the nav ─────────────────────────────────────────────────────────────────
const nav = byId('nav');

function onScroll(): void {
  nav.classList.toggle('is-stuck', scrollY > 8);
}
addEventListener('scroll', onScroll, { passive: true });
onScroll();

// ── reveal on scroll ────────────────────────────────────────────────────────
// Once, not toggled: a section that fades back out as it leaves is a section
// that flickers while the reader scrolls back to check something.
const revealed = document.querySelectorAll<HTMLElement>('.reveal');
if (!isStill && 'IntersectionObserver' in globalThis) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.12 },
  );
  for (const node of revealed) observer.observe(node);
} else {
  for (const node of revealed) node.classList.add('is-in');
}

// ── bubbles ─────────────────────────────────────────────────────────────────
// A handful per container, each with its own size, lane and pace, so no two
// rise in step. Sized in CSS pixels rather than percentages: a 6 px bubble in
// the hero window should be a 6 px bubble on the section behind it too.
function fillBubbles(container: HTMLElement): void {
  const count = Number(container.dataset.bubbles) || 10;
  for (let index = 0; index < count; index++) {
    const bubble = document.createElement('span');
    bubble.className = 'bubble';
    const size = random(4, 16);
    bubble.style.width = String(size) + 'px';
    bubble.style.height = String(size) + 'px';
    bubble.style.left = String(random(2, 98)) + '%';
    bubble.style.animationDuration = String(random(9, 22)) + 's';
    bubble.style.animationDelay = String(-random(0, 22)) + 's';
    bubble.style.setProperty('--sway', String(random(-40, 40)) + 'px');
    container.append(bubble);
  }
}
if (!isStill) {
  for (const container of document.querySelectorAll<HTMLElement>('[data-bubbles]')) {
    fillBubbles(container);
  }
}

// ── the shoal ───────────────────────────────────────────────────────────────
// Origami fish drifting through the hero behind the copy. The markup is the
// same polygons as the `#ori-fish` symbol in the page, inlined rather than
// <use>d so the tail can be animated by an ordinary class selector.
const ORIGAMI =
  '<svg viewBox="0 0 120 80" aria-hidden="true">' +
  '<g class="tail">' +
  '<polygon points="2,12 42,40 20,40" fill="#a9c5e0"/>' +
  '<polygon points="2,68 42,40 20,40" fill="#8db3d6"/>' +
  '<polygon points="20,40 42,40 30,54" fill="#6f9bc4"/>' +
  '</g>' +
  '<polygon points="64,24 84,2 94,22" fill="#a9c5e0"/>' +
  '<polygon points="36,40 82,12 112,40" fill="#eef3f9"/>' +
  '<polygon points="36,40 112,40 80,70" fill="#a9c5e0"/>' +
  '<polygon points="82,12 112,40 96,40" fill="#c6d8ea"/>' +
  '<polygon points="60,40 82,12 84,58" fill="#dfe9f3" opacity="0.7"/>' +
  '<circle cx="100" cy="36" r="3.2" fill="#2b3a4a"/>' +
  '</svg>';

type Swimmer = { size: number; y: number; time: number; alpha: number; blur: number };

// Far fish are small, faint, slow and slightly out of focus; near ones are the
// opposite. Depth is the whole illusion, so the four numbers move together.
function swimmerAt(depth: number): Swimmer {
  return {
    size: 34 + depth * 110,
    y: random(4, 88),
    time: 70 - depth * 38,
    alpha: 0.25 + depth * 0.6,
    blur: (1 - depth) * 1.6,
  };
}

function spawnShoal(container: HTMLElement, count: number): void {
  for (let index = 0; index < count; index++) {
    const depth = Math.random();
    const fish = swimmerAt(depth);
    const node = document.createElement('div');
    node.className = 'ori' + (Math.random() < 0.3 ? ' flip' : '');
    node.style.setProperty('--size', String(Math.round(fish.size)) + 'px');
    node.style.setProperty('--y', fish.y.toFixed(1) + '%');
    node.style.setProperty('--x', random(5, 85).toFixed(1) + '%');
    node.style.setProperty('--time', fish.time.toFixed(1) + 's');
    node.style.setProperty('--delay', (-random(0, fish.time)).toFixed(1) + 's');
    node.style.setProperty('--alpha', fish.alpha.toFixed(2));
    node.style.setProperty('--blur', fish.blur.toFixed(2) + 'px');
    node.style.zIndex = depth > 0.7 ? '3' : '1';
    node.innerHTML = ORIGAMI;
    container.append(node);
  }
}
const shoal = maybe('shoal');
if (shoal) spawnShoal(shoal, innerWidth < 720 ? 6 : 10);

// ── parallax over the hero stage ────────────────────────────────────────────
// Mouse only. On a phone the stage scrolls under a thumb, and a layer that
// moves with the finger reads as a broken scroll rather than as depth.
const stage = maybe('stage');
if (stage && canHover && !isStill) {
  const layers = [...stage.querySelectorAll<HTMLElement>('[data-depth]')];
  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  let isTicking = false;

  const tick = (): void => {
    currentX += (targetX - currentX) * 0.08;
    currentY += (targetY - currentY) * 0.08;
    for (const layer of layers) {
      const depth = Number(layer.dataset.depth) || 0;
      const base = layer.dataset.baseTransform ?? '';
      layer.style.transform =
        base +
        ' translate(' +
        (currentX * depth * 14).toFixed(1) +
        'px, ' +
        (currentY * depth * 14).toFixed(1) +
        'px)';
    }
    if (Math.abs(targetX - currentX) + Math.abs(targetY - currentY) > 0.002) {
      requestAnimationFrame(tick);
    } else {
      isTicking = false;
    }
  };
  const nudge = (): void => {
    if (isTicking) return;
    isTicking = true;
    requestAnimationFrame(tick);
  };

  // Each layer's resting rotation lives in the stylesheet; it is read once so
  // the parallax translation is added to it rather than replacing it.
  for (const layer of layers) {
    const resting = getComputedStyle(layer).transform;
    layer.dataset.baseTransform = resting === 'none' ? '' : resting;
    layer.style.transition = 'none';
  }
  stage.parentElement?.addEventListener('pointermove', (event) => {
    const box = stage.getBoundingClientRect();
    targetX = ((event.clientX - box.left) / box.width - 0.5) * 2;
    targetY = ((event.clientY - box.top) / box.height - 0.5) * 2;
    nudge();
  });
  stage.parentElement?.addEventListener('pointerleave', () => {
    targetX = 0;
    targetY = 0;
    nudge();
  });
}

// ── the contact form ────────────────────────────────────────────────────────
type LeadReply = { ok?: boolean; error?: string; retryAfter?: number };

type LeadOutcome = { status: number; reply: LeadReply };

// A network failure is status 0: the same branch as an unexpected 5xx, because
// what the visitor can do about either is identical — try again in a moment.
async function postLead(body: string): Promise<LeadOutcome> {
  try {
    const response = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    let reply: LeadReply = {};
    try {
      reply = (await response.json()) as LeadReply;
    } catch {
      // A non-JSON body is still a refusal; the copy below covers it.
    }
    return { status: response.status, reply };
  } catch {
    return { status: 0, reply: {} };
  }
}

function minutesFrom(seconds: number | undefined): string {
  return String(Math.max(1, Math.ceil((seconds ?? 60) / 60)));
}

// The analytics tag, when the server injected one (core/analytics.ts). A
// page view says somebody came; this says they asked to be called, which is
// the number the landing page exists to move. GA4 counts `generate_lead` as a
// conversion out of the box.
type Gtag = (command: 'event', name: string, parameters?: Record<string, string>) => void;

function reportLead(): void {
  const gtag = (globalThis as { gtag?: Gtag }).gtag;
  if (typeof gtag === 'function') gtag('event', 'generate_lead');
}

const form = maybe('lead-form');
if (form instanceof HTMLFormElement) {
  const error = byId('lead-error');
  const submit = byId('lead-submit');
  const contact = input('lead-contact');

  const say = (message: string): void => {
    error.textContent = message;
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    say('');
    if (contact.value.trim() === '') {
      say('Leave an email address or a phone number so we can reach you.');
      contact.focus();
      return;
    }
    const data = new FormData(form);
    const body = JSON.stringify({
      name: data.get('name'),
      contact: data.get('contact'),
      message: data.get('message'),
      website: data.get('website'),
    });
    submit.setAttribute('disabled', '');
    submit.textContent = 'Sending…';

    void postLead(body).then(({ status, reply }) => {
      submit.removeAttribute('disabled');
      submit.textContent = 'Ask us to get in touch';

      if (status === 200 && reply.ok) {
        form.closest('.contact')?.classList.add('is-done');
        byId('lead-done').focus();
        reportLead();
        return;
      }
      if (status === 429) {
        say(
          'That is a few in a row — please try again in about ' +
            minutesFrom(reply.retryAfter) +
            ' min.',
        );
        return;
      }
      if (status === 400 && reply.error) {
        say(reply.error.charAt(0).toUpperCase() + reply.error.slice(1) + '.');
        contact.focus();
        return;
      }
      say('Something went wrong on our side. Please try again in a moment.');
    });
  });
}
